import { useState } from 'react';
import {
  CALL_OUTCOME_DESCRIPTIONS,
  CALL_OUTCOME_EFFECTS,
  CALL_OUTCOME_FAMILIES,
  CALL_OUTCOME_LABELS
} from '../../services/prospectService';

/**
 * Les 15 issues d'appel en 4 familles (§5.7).
 *
 * Deux verrous d'interface :
 *  - **« Enregistrer » reste désactivé tant qu'aucune issue n'est choisie** : aucun appel ne peut
 *    être enregistré sans issue (§4.2, règle 5) ;
 *  - `opposition` demande une **confirmation en deux temps** : c'est le seul bouton qui écrase un
 *    statut positif et il est irréversible depuis l'UI.
 *
 * Le seul champ conditionnel est `needsDate` (issue `rappeler`). Aucune règle n'est dupliquée dans
 * ce composant : tout vient de `CALL_OUTCOME_EFFECTS`.
 */

const CLASSES_FAMILLE = {
  A: 'btn-ghost',
  B: 'btn-outline',
  C: 'btn-outline btn-success',
  D: 'btn-outline btn-error'
};

const versDate = (valeur) => (valeur ? new Date(valeur) : null);

const CallOutcomeForm = ({ prospect = {}, onSubmit, enCours = false }) => {
  const [outcome, setOutcome] = useState('');
  const [note, setNote] = useState('');
  const [dateRappel, setDateRappel] = useState('');
  const [motif, setMotif] = useState('');
  const [confirmationOpposition, setConfirmationOpposition] = useState(false);

  const effet = CALL_OUTCOME_EFFECTS[outcome] || null;

  const champManquant =
    Boolean(effet?.needsDate && !dateRappel) || (outcome === 'opposition' && !motif);

  const inactive = !outcome || champManquant || enCours;

  const choisir = (issue) => {
    setOutcome(issue);
    setConfirmationOpposition(false);
  };

  const soumettre = async (evenement) => {
    evenement.preventDefault();
    if (inactive) return;

    // `opposition` : premier clic = demande de confirmation, second = écriture
    if (outcome === 'opposition' && !confirmationOpposition) {
      setConfirmationOpposition(true);
      return;
    }

    await onSubmit(outcome, {
      note: note || null,
      nextCallAt: versDate(dateRappel),
      motif: motif || null
    });

    setOutcome('');
    setNote('');
    setDateRappel('');
    setMotif('');
    setConfirmationOpposition(false);
  };

  return (
    <form onSubmit={soumettre} className="space-y-4">
      {CALL_OUTCOME_FAMILIES.map((famille) => (
        <div key={famille.id}>
          <h4 className="text-sm font-semibold mb-2 text-base-content/70">
            {famille.id} — {famille.titre}
          </h4>
          <div className="flex flex-wrap gap-2">
            {famille.issues.map((issue) => (
              <button
                key={issue}
                type="button"
                title={CALL_OUTCOME_DESCRIPTIONS[issue]}
                className={`btn btn-sm ${CLASSES_FAMILLE[famille.id] || 'btn-outline'} ${
                  outcome === issue ? 'btn-active ring-2 ring-primary' : ''
                }`}
                onClick={() => choisir(issue)}
              >
                {CALL_OUTCOME_LABELS[issue]}
              </button>
            ))}
          </div>
        </div>
      ))}
      {/* CHAMPS */}
      {/* Champs conditionnels — pilotés par la table, jamais codés en dur */}
      {effet?.needsDate && (
        <label className="form-control w-full max-w-xs">
          <span className="label-text">Date et heure du rappel (obligatoire)</span>
          <input
            type="datetime-local"
            className="input input-bordered input-sm"
            value={dateRappel}
            onChange={(evenement) => setDateRappel(evenement.target.value)}
            required
          />
        </label>
      )}

      {outcome === 'opposition' && (
        <label className="form-control w-full max-w-md">
          <span className="label-text">Motif de l’opposition (obligatoire)</span>
          <input
            type="text"
            className="input input-bordered input-sm"
            value={motif}
            onChange={(evenement) => setMotif(evenement.target.value)}
            placeholder="« Ne plus être contacté »"
            required
          />
        </label>
      )}

      <label className="form-control w-full">
        <span className="label-text">Note libre</span>
        <textarea
          className="textarea textarea-bordered textarea-sm"
          rows={2}
          value={note}
          onChange={(evenement) => setNote(evenement.target.value)}
          placeholder="Ce qui s’est dit pendant l’appel…"
        />
      </label>

      {confirmationOpposition && (
        <div className="alert alert-warning text-sm">
          <span>
            Cette fiche sera définitivement retirée de toutes les files (RGPD). Cliquez à nouveau
            sur « Confirmer » pour valider.
          </span>
        </div>
      )}

      <div className="flex items-center gap-3 flex-wrap">
        <button type="submit" className="btn btn-primary btn-sm" disabled={inactive}>
          {enCours
            ? 'Enregistrement…'
            : confirmationOpposition
            ? 'Confirmer l’opposition'
            : 'Enregistrer'}
        </button>
        {!outcome && (
          <span className="text-sm text-base-content/60">
            Choisissez une issue pour activer l’enregistrement.
          </span>
        )}
        {prospect.callAttempts >= 3 && (
          <span className="badge badge-error badge-sm">3 tentatives épuisées</span>
        )}
      </div>
    </form>
  );
};

export default CallOutcomeForm;
